/**
 * Quote Tool for Editor.js
 * Based on @editorjs/quote with caption toggle feature
 */
import './index.css';
import { IconAlignLeft, IconAlignCenter, IconQuote } from '@codexteam/icons';
import { make } from '@/editorjs/utils';

/**
 * Alignment enum
 */
const Alignment = {
  Left: 'left',
  Center: 'center',
};

/**
 * Caption icon SVG
 */
const IconCaption = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M7 10H17"/>
  <path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M7 14H12"/>
  <rect width="14" height="14" x="5" y="5" stroke="currentColor" stroke-width="2" rx="4"/>
</svg>`;

/**
 * @class Quote
 * @classdesc Quote Tool for Editor.js with optional caption
 */
export default class Quote {
  /**
   * The Editor.js API
   */
  api;

  /**
   * Is Quote Tool read-only
   */
  readOnly;

  /**
   * Placeholder for Quote Tool
   */
  _quotePlaceholder;

  /**
   * Current quote element
   */
  _block;

  /**
   * Caption placeholder for Quote Tool
   */
  _captionPlaceholder;

  /**
   * Quote's data
   */
  _data;

  /**
   * Quote Tool's CSS classes
   */
  _CSS;

  /**
   * Caption element reference
   */
  _captionElement;

  /**
   * Quote text element reference
   */
  _textElement;

  /**
   * Render plugin`s main Element and fill it with saved data
   *
   * @param {object} params - constructor params
   * @param {QuoteData} params.data - previously saved data
   * @param {QuoteConfig} params.config - user config for Tool
   * @param {API} params.api - editor.js api
   * @param {boolean} params.readOnly - read only mode flag
   */
  constructor({ data, config, api, readOnly, block }) {
    const { DEFAULT_ALIGNMENT } = Quote;

    this.api = api;
    this.readOnly = readOnly;

    this._quotePlaceholder =
      config?.quotePlaceholder || Quote.DEFAULT_QUOTE_PLACEHOLDER;
    this._captionPlaceholder =
      config?.captionPlaceholder || Quote.DEFAULT_CAPTION_PLACEHOLDER;

    // caption이 있으면 showCaption을 true로, 없으면 false로 설정
    const hasExistingCaption = data.caption && data.caption.trim().length > 0;

    this._data = {
      text: data.text || '',
      caption: data.caption || '',
      alignment:
        (Object.values(Alignment).includes(data.alignment) && data.alignment) ||
        config?.defaultAlignment ||
        DEFAULT_ALIGNMENT,
      showCaption: hasExistingCaption, // 기존 caption이 있으면 true
    };

    this._CSS = {
      baseClass: this.api.styles.block,
      wrapper: 'cdx-quote',
      text: 'cdx-quote__text',
      input: this.api.styles.input,
      caption: 'cdx-quote__caption',
    };
    this._block = block;
  }

  /**
   * Notify core that read-only mode is supported
   *
   * @returns {boolean}
   */
  static get isReadOnlySupported() {
    return true;
  }

  /**
   * Get Tool toolbox settings
   * icon - Tool icon's SVG
   * title - title to show in toolbox
   *
   * @returns {{icon: string, title: string}}
   */
  static get toolbox() {
    return {
      icon: IconQuote,
      title: 'Quote',
    };
  }

  /**
   * Empty Quote is not empty Block
   *
   * @public
   * @returns {boolean}
   */
  static get contentless() {
    return true;
  }

  /**
   * Allow to press Enter inside the Quote
   *
   * @public
   * @returns {boolean}
   */
  static get enableLineBreaks() {
    return true;
  }

  /**
   * Default placeholder for quote text
   *
   * @public
   * @returns {string}
   */
  static get DEFAULT_QUOTE_PLACEHOLDER() {
    return 'Enter a quote';
  }

  /**
   * Default placeholder for quote caption
   *
   * @public
   * @returns {string}
   */
  static get DEFAULT_CAPTION_PLACEHOLDER() {
    return 'Enter a caption';
  }

  /**
   * Default quote alignment
   *
   * @public
   * @returns {string}
   */
  static get DEFAULT_ALIGNMENT() {
    return Alignment.Left;
  }

  /**
   * Allow Quote to be converted to/from other blocks
   */
  static get conversionConfig() {
    return {
      /**
       * To create Quote data from string, simple fill 'text' property
       */
      import: 'text',
      /**
       * To create string from Quote data, concatenate text and caption
       *
       * @param {QuoteData} quoteData
       * @returns {string}
       */
      export: function (quoteData) {
        return quoteData.caption
          ? `${quoteData.text} — ${quoteData.caption}`
          : quoteData.text;
      },
    };
  }

  /**
   * Tool`s styles
   *
   * @returns {QuoteCSS}
   */
  get CSS() {
    return {
      baseClass: this.api.styles.block,
      wrapper: 'cdx-quote',
      text: 'cdx-quote__text',
      input: this.api.styles.input,
      caption: 'cdx-quote__caption',
    };
  }

  /**
   * Tool`s settings properties
   *
   * @returns {Array}
   */
  get settings() {
    return [
      {
        name: Alignment.Left,
        icon: IconAlignLeft,
      },
      {
        name: Alignment.Center,
        icon: IconAlignCenter,
      },
    ];
  }

  /**
   * Create Quote Tool container with inputs
   *
   * @returns {HTMLElement}
   */
  render() {
    const container = make('blockquote', [
      this._CSS.baseClass,
      this._CSS.wrapper,
      `cdx-quote--${this._data.alignment}`,
    ]);

    const quote = make('div', [this._CSS.input, this._CSS.text], {
      contentEditable: !this.readOnly,
      innerHTML: this._data.text,
    });

    const caption = make('div', [this._CSS.input, this._CSS.caption], {
      contentEditable: !this.readOnly,
      innerHTML: this._data.caption,
    });

    quote.dataset.placeholder = this._quotePlaceholder;
    caption.dataset.placeholder = this._captionPlaceholder;

    // Caption visibility based on showCaption
    if (!this._data.showCaption) {
      caption.style.display = 'none';
    }

    this._textElement = quote;
    this._captionElement = caption;
    this._container = container;

    // 블록이 선택될 때 커서 위치 처리 (container 자체 클릭 시)
    container.addEventListener('click', (event) => {
      if (event.target === container) {
        this._setCursorToEnd();
      }
    });

    // IME composition 상태 추적
    let isComposing = false;

    // Enter 키 이벤트 바인딩
    if (!this.readOnly) {
      [quote, caption].forEach((element) => {
        // Composition 이벤트 리스너
        element.addEventListener('compositionstart', () => {
          isComposing = true;
        });

        element.addEventListener('compositionend', () => {
          isComposing = false;
        });

        element.addEventListener('keydown', (event) => {
          // IME 입력 중에는 Enter 처리하지 않음
          if (isComposing) return;

          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            if (event.shiftKey) {
              // Shift+Enter: 본문 내 줄바꿈
              this.insertLineBreak();
            } else {
              // Enter: Quote 블록을 벗어나서 다음 블록으로 이동
              const currentBlockIndex = this.api.blocks.getCurrentBlockIndex();
              this.api.blocks.insert('paragraph', { text: '' }, undefined, currentBlockIndex + 1, true);
              this.api.caret.setToBlock(currentBlockIndex + 1, 'start');
            }
          }

          // Caption에서 Backspace: 비어있으면 caption 숨기고 본문으로 이동
          if (event.key === 'Backspace' && element === caption) {
            const captionContent = caption.textContent?.trim() || '';
            if (captionContent === '') {
              event.preventDefault();
              event.stopPropagation();

              // Caption 숨기기
              this._data.showCaption = false;
              caption.style.display = 'none';
              caption.innerHTML = '';

              // 본문 끝으로 커서 이동
              this._setCursorToEnd();

              this._block.dispatchChange();
            }
          }
        });
      });
    }

    container.appendChild(quote);
    container.appendChild(caption);
    return container;
  }

  /**
   * Extract Quote data from Quote Tool element
   *
   * @param {HTMLDivElement} quoteElement - element to save
   * @returns {QuoteData}
   */
  save(quoteElement) {
    const text = quoteElement.querySelector(`.${this._CSS.text}`);
    const caption = quoteElement.querySelector(`.${this._CSS.caption}`);

    const captionText = caption?.innerHTML ?? '';
    const captionHasContent = captionText.trim().length > 0 && captionText.trim() !== '<br>';

    // 가시성은 DOM 상태를 진실로 삼는다 — collab에서 remote가 caption을 채워 넣어도
    // 이 인스턴스의 _data.showCaption은 생성자 기준 값으로 고정되어 있어 신뢰할 수 없다.
    const captionVisible = caption ? caption.style.display !== 'none' : false;

    return {
      text: text?.innerHTML ?? '',
      caption: (captionVisible && captionHasContent) ? captionText : '',
      alignment: this._data.alignment,
      // showCaption은 저장하지 않음 - caption 유무로 판단
    };
  }

  /**
   * Handle merge from another block (e.g., when backspace is pressed on empty paragraph after quote)
   * Sets cursor to end of quote text
   *
   * @param {object} data - data from merged block
   */
  merge(data) {
    // Append any text from the merged block to our text
    if (data.text) {
      this._data.text += data.text;
      if (this._textElement) {
        this._textElement.innerHTML = this._data.text;
      }
    }

    // Set cursor to end of text
    this._setCursorToEnd();
  }

  /**
   * Called by Editor.js after block is rendered
   */
  rendered() {
    if (this._block?.holder) {
      // document 레벨에서 keyup 이벤트 감지 (backspace 처리 - 블록 삭제 후 상태 확인)
      this._handleDocumentKeyup = (event) => {
        if (event.key === 'Backspace') {
          const currentBlockIndex = this.api.blocks.getCurrentBlockIndex();
          const currentBlock = this.api.blocks.getBlockByIndex(currentBlockIndex);

          // 현재 블록이 이 quote이고, 포커스가 body에 있는 경우 (포커스 손실)
          if (currentBlock?.id === this._block?.id) {
            const activeElement = document.activeElement;
            const isBodyFocused = activeElement === document.body || activeElement?.tagName === 'BODY';
            const isNotInsideQuote = !this._block.holder.contains(activeElement);

            if (isBodyFocused || isNotInsideQuote) {
              this._setCursorToEnd();
            }
          }
        }
      };

      document.addEventListener('keyup', this._handleDocumentKeyup);
    }
  }

  /**
   * Set cursor to end of text element
   * @private
   */
  _setCursorToEnd() {
    if (!this._textElement || !document.body.contains(this._textElement)) return;

    this._textElement.focus();

    try {
      const selection = window.getSelection();
      const range = document.createRange();

      range.selectNodeContents(this._textElement);
      range.collapse(false);

      selection.removeAllRanges();
      selection.addRange(range);
    } catch {
      // DOM이 교체된 경우 무시 (collab remote sync 등)
    }
  }

  /**
   * Insert line break at cursor position
   */
  insertLineBreak() {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) {
      return;
    }

    try {
      const range = selection.getRangeAt(0);
      const br = document.createElement('br');

      range.insertNode(br);
      range.setStartAfter(br);
      range.setEndAfter(br);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch {
      // DOM이 교체된 경우 무시
    }
  }

  /**
   * Sanitizer rules
   */
  static get sanitize() {
    return {
      text: {
        br: true,
      },
      caption: {
        br: true,
      },
      alignment: {},
    };
  }

  /**
   * Toggle caption visibility
   */
  _toggleCaption() {
    this._data.showCaption = !this._data.showCaption;

    if (this._captionElement) {
      if (this._data.showCaption) {
        this._captionElement.style.display = '';
        // Focus on caption when shown
        setTimeout(() => {
          this._captionElement.focus();
        }, 0);
      } else {
        this._captionElement.style.display = 'none';
        // Clear caption content when hidden
        this._captionElement.innerHTML = '';
      }
    }

    this._block.dispatchChange();
  }

  /**
   * Create wrapper for Tool`s settings buttons:
   * 1. Left alignment
   * 2. Center alignment
   * 3. Caption toggle
   *
   * @returns {MenuConfig}
   */
  renderSettings() {
    const capitalize = (str) =>
      str ? str[0].toUpperCase() + str.slice(1) : str;

    const alignmentSettings = this.settings.map((item) => ({
      icon: item.icon,
      label: this.api.i18n.t(`Align ${capitalize(item.name)}`),
      onActivate: () => this._toggleTune(item.name),
      isActive: this._data.alignment === item.name,
      closeOnActivate: true,
    }));

    // Caption toggle menu item
    const captionToggle = {
      icon: IconCaption,
      label: this.api.i18n.t('Caption'),
      onActivate: () => this._toggleCaption(),
      isActive: this._data.showCaption,
      closeOnActivate: true,
    };

    return [...alignmentSettings, captionToggle];
  }

  /**
   * Toggle quote`s alignment
   *
   * @param {string} tune - alignment
   * @private
   */
  _toggleTune(tune) {
    this._data.alignment = tune;

    const quoteElement = this._block.holder.querySelector('blockquote');

    if (quoteElement) {
      quoteElement.classList.remove('cdx-quote--left', 'cdx-quote--center');
      quoteElement.classList.add(`cdx-quote--${tune}`);
    }

    this._block.dispatchChange();
  }
}
